import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ImageOff } from 'lucide-react';
import api from '../../lib/api';

interface Photo {
  id: string;
  url: string;
  type: string;
}

export default function PhotoThumbnails({ visitId }: { visitId: string }) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);

  const { data: photos, isLoading } = useQuery<Photo[]>({
    queryKey: ['visit-photos', visitId],
    queryFn: async () => {
      const res = await api.get(`/visits/${visitId}`);
      return res.data.data.photos || [];
    },
    staleTime: 60000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!photos || photos.length === 0) {
    return (
      <div className="text-center py-4">
        <ImageOff size={24} className="mx-auto text-gray-300 mb-1" />
        <p className="text-xs text-gray-400">Sem fotos</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo) => (
          <button
            key={photo.id}
            onClick={() => setSelectedPhoto(photo)}
            className="aspect-square rounded-lg overflow-hidden bg-gray-100 active:opacity-80 transition-opacity"
          >
            <img
              src={photo.url}
              alt={photo.type}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Full-screen photo modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPhoto(null);
            }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white active:bg-white/20 z-10"
            aria-label="Fechar"
          >
            <X size={24} />
          </button>
          <img
            src={selectedPhoto.url}
            alt={selectedPhoto.type}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs bg-black/40 px-3 py-1 rounded-full">
            {selectedPhoto.type}
          </span>
        </div>
      )}
    </>
  );
}
